(() => {
  if (customElements.get("subscription-product-offer")) return;

  const wait = (duration) => new Promise((resolve) => setTimeout(resolve, duration));

  class SubscriptionProductOffer extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector(".nektar-subscription__checkbox");
      this.select = this.querySelector(".nektar-subscription__select");
      this.form = document.getElementById(this.dataset.formId);
      this.productInfo = this.closest(".product-info");
      this.section = this.closest(".shopify-section");
      this.mode = this.dataset.mode;
      this.plans = JSON.parse(this.querySelector("[data-subscription-plans]")?.textContent || "[]");
      this.submitting = false;
      this.complementaryPassThrough = false;
      this.onQuantityChangeBound ||= () => this.syncMoonQuantity();

      if (!this.checkbox || !this.select || !this.form || !this.productInfo || !this.plans.length) return;

      if (this.mode === "bundle_component") this.hidden = true;
      this.productInfo.classList.add("nektar-product-offers");
      this.disableKaching();
      this.applyThemeVariables();
      this.moveAfterEmbeddedMoonBundle();
      this.decorateMoonWhenReady();
      this.decorateButton();

      this.checkbox.addEventListener("change", () => this.onSubscriptionChange());
      this.select.addEventListener("change", () => this.onPlanChange());
      this.form.addEventListener("submit", (event) => this.onSubmit(event), true);
      this.productInfo.addEventListener("click", (event) => this.onMoonClick(event));

      this.mutationObserver = new MutationObserver(() => {
        this.disableKaching();
        this.decorateMoon();
        this.syncBundleComponentVisibility();
        this.updateButtonPrice();
      });
      this.mutationObserver.observe(this.productInfo, { childList: true, subtree: true, attributes: true });

      this.onPlanChange();
      this.updateButtonPrice();
    }

    disableKaching() {
      this.productInfo
        .querySelectorAll("kaching-bundle, kaching-bundles-block")
        .forEach((element) => element.remove());

      this.form
        .querySelectorAll('input[name="properties[__kaching_bundles]"]')
        .forEach((input) => input.remove());

      const buyButtons = this.form.querySelector("buy-buttons.buy-buttons--with-quantity");
      const quantityWrapper = buyButtons?.querySelector(".buy-buttons__quantity");
      const quantitySelector = buyButtons?.querySelector("quantity-selector");
      const quantityInput = quantitySelector?.querySelector(".quantity-selector__input");
      const submitButton = this.form.querySelector('button[type="submit"]');

      if (this.quantityInput !== quantityInput) {
        this.quantityInput?.removeEventListener("input", this.onQuantityChangeBound);
        this.quantityInput?.removeEventListener("change", this.onQuantityChangeBound);
        this.quantityInput = quantityInput;
        this.quantityInput?.addEventListener("input", this.onQuantityChangeBound);
        this.quantityInput?.addEventListener("change", this.onQuantityChangeBound);
      }

      this.restoreNativeControl(this.productInfo, "block");
      this.restoreNativeControl(this.form, "block");
      this.restoreNativeControl(buyButtons, "grid");
      this.restoreNativeControl(quantityWrapper, "flex");
      this.restoreNativeControl(quantitySelector, "inline-flex");
      quantitySelector
        ?.querySelectorAll(".quantity-selector__button")
        .forEach((button) => this.restoreNativeControl(button, "block"));
      this.restoreNativeControl(quantityInput, "block");
      this.restoreNativeControl(submitButton, "block");

      if (quantityInput?.disabled) {
        quantityInput.disabled = false;
      }
    }

    restoreNativeControl(element, display) {
      if (!element) return;

      element.hidden = false;

      if (
        element.style.getPropertyValue("display") !== display
        || element.style.getPropertyPriority("display") !== "important"
      ) {
        element.style.setProperty("display", display, "important");
      }

      if (
        element.style.getPropertyValue("visibility") !== "visible"
        || element.style.getPropertyPriority("visibility") !== "important"
      ) {
        element.style.setProperty("visibility", "visible", "important");
      }

      if (
        element.style.getPropertyValue("opacity") !== "1"
        || element.style.getPropertyPriority("opacity") !== "important"
      ) {
        element.style.setProperty("opacity", "1", "important");
      }
    }

    disconnectedCallback() {
      this.mutationObserver?.disconnect();
      this.quantityInput?.removeEventListener("input", this.onQuantityChangeBound);
      this.quantityInput?.removeEventListener("change", this.onQuantityChangeBound);
      this.quantityInput = null;
    }

    applyThemeVariables() {
      for (const property of [
        "--nektar-accent",
        "--nektar-accent-dark",
        "--nektar-selected-bg",
        "--nektar-sub-button-bg",
        "--nektar-sub-button-color",
        "--nektar-third-compare-price"
      ]) {
        const value = this.style.getPropertyValue(property);
        if (value) this.productInfo.style.setProperty(property, value);
      }
    }

    async moveAfterEmbeddedMoonBundle() {
      for (let attempt = 0; attempt < 40; attempt += 1) {
        const buyButtons = this.productInfo.querySelector(".product-info__buy-buttons");
        const moon = buyButtons?.querySelector("moonbundle-component");
        const form = buyButtons?.querySelector('form[is="product-form"]');

        if (moon && form && this.parentElement !== buyButtons) {
          buyButtons.insertBefore(this, form);
          return;
        }

        if (moon || this.previousElementSibling?.matches?.(".shopify-app-block")) return;
        await wait(100);
      }
    }

    async decorateMoonWhenReady() {
      for (let attempt = 0; attempt < 50; attempt += 1) {
        if (this.decorateMoon()) {
          this.syncBundleComponentVisibility();
          this.syncMoonQuantity();
          return;
        }
        await wait(100);
      }
    }

    decorateMoon() {
      const wrappers = [...this.productInfo.querySelectorAll("moonbundle-component .moonbundle-option-wrapper")];
      if (!wrappers.length) return false;

      const offerDetails = [
        {
          duration: this.dataset.firstOfferDuration,
          quantity: this.dataset.firstOfferQuantity,
          days: Number(this.dataset.firstOfferDays)
        },
        {
          duration: this.dataset.secondOfferDuration,
          quantity: this.dataset.secondOfferQuantity,
          days: Number(this.dataset.secondOfferDays)
        },
        {
          detail: this.dataset.thirdOfferDetail,
          duration: this.dataset.thirdOfferDuration,
          quantity: this.dataset.thirdOfferQuantity
        }
      ];

      wrappers.slice(0, 3).forEach((wrapper, index) => {
        const column = wrapper.querySelector(".moonbundle-option-col");
        const price = wrapper.querySelector(".moonbundle-option-price");
        const bestSeller = wrapper.querySelector(".moonbundle-best-seller-left");
        const moonRadio = wrapper.querySelector(".moonbundle-bullet");
        const details = offerDetails[index] || {};
        const { detail, duration, quantity, days } = details;

        if (index === 2 && !wrapper.classList.contains("nektar-moon-kit-offer")) {
          wrapper.classList.add("nektar-moon-kit-offer");
        }

        if (
          moonRadio
          && (
            moonRadio.style.getPropertyValue("display") !== "none"
            || moonRadio.style.getPropertyPriority("display") !== "important"
          )
        ) {
          moonRadio.style.setProperty("display", "none", "important");
        }

        if (bestSeller && bestSeller.textContent.trim() !== "Meilleure Vente") {
          bestSeller.textContent = "Meilleure Vente";
        }

        if (column && !column.querySelector(".nektar-moon-meta") && (detail || duration || quantity)) {
          const meta = document.createElement("div");
          meta.className = "nektar-moon-meta";
          meta.innerHTML = [
            detail ? `<span class="nektar-moon-meta__detail"><strong>${this.escapeHtml(detail)}</strong></span>` : "",
            duration ? `<span><strong>Durée :</strong> ${this.escapeHtml(duration)}</span>` : "",
            quantity ? `<span><strong>Quantité :</strong> ${this.escapeHtml(quantity)}</span>` : ""
          ].join("");
          column.append(meta);
        }

        if (price && !price.querySelector(".nektar-moon-daily-price") && days > 0) {
          const cents = this.parseMoney(price.querySelector(".moonbundle-option-single-price")?.textContent);
          if (cents > 0) {
            const daily = document.createElement("span");
            daily.className = "nektar-moon-daily-price";
            daily.textContent = `${this.formatMoney(Math.round(cents / days))}/jour`;
            price.append(daily);
          }
        }

      });

      return true;
    }

    syncBundleComponentVisibility() {
      if (this.mode !== "bundle_component") return;

      const wrappers = [...this.productInfo.querySelectorAll(
        "moonbundle-component .moonbundle-option-wrapper"
      )];
      const shouldShow = wrappers[1]?.querySelector(".moonbundle-option")?.classList
        .contains("moonbundle-option-active") === true;

      if (this.hidden === shouldShow) {
        this.hidden = !shouldShow;
      }

      if (!shouldShow && this.checkbox.checked) {
        this.checkbox.checked = false;
        this.onSubscriptionChange();
      }
    }

    decorateButton() {
      const button = this.form.querySelector('button[type="submit"]');
      if (!button || button.querySelector(".nektar-atc-content")) return;

      const label = button.querySelector(":scope > div");
      if (!label) return;

      label.className = "nektar-atc-content";
      label.innerHTML = `
        <span class="nektar-atc-label">${this.escapeHtml(label.textContent.trim())}</span>
        <span class="nektar-atc-divider" aria-hidden="true"></span>
        <span class="nektar-atc-price"></span>
      `;
      this.buttonPrice = label.querySelector(".nektar-atc-price");
    }

    onSubscriptionChange() {
      const selected = this.checkbox.checked;
      this.classList.toggle("is-selected", selected);
      this.productInfo.classList.toggle("nektar-subscription-selected", selected);
      this.productInfo.classList.toggle("is-subscription-exclusive", selected && this.mode === "exclusive");
      this.querySelector(".nektar-subscription__benefits")?.setAttribute("aria-hidden", String(!selected));
      this.updateButtonPrice();
    }

    onPlanChange() {
      const option = this.select.selectedOptions[0];
      if (!option) return;

      const price = Number(option.dataset.price);
      const comparePrice = Number(option.dataset.comparePrice);
      const discount = option.dataset.discount;

      this.querySelector(".nektar-subscription__price").textContent = this.formatMoney(price);
      this.querySelector(".nektar-subscription__compare-price").textContent = this.formatMoney(comparePrice);
      const discountPill = this.querySelector(".nektar-subscription__discount");
      if (discountPill) discountPill.textContent = `−${discount}%`;
      const benefitDiscount = this.querySelector("[data-subscription-discount]");
      if (benefitDiscount) benefitDiscount.textContent = discount;
      this.updateButtonPrice();
    }

    onMoonClick(event) {
      if (!event.target.closest("moonbundle-component .moonbundle-option")) return;

      if (this.mode === "exclusive" && this.checkbox.checked) {
        this.checkbox.checked = false;
        this.onSubscriptionChange();
      }

      requestAnimationFrame(() => {
        this.syncBundleComponentVisibility();
        this.syncMoonQuantity();
        this.updateButtonPrice();
      });
      setTimeout(() => {
        this.syncBundleComponentVisibility();
        this.syncMoonQuantity();
        this.updateButtonPrice();
      }, 80);
    }

    syncMoonQuantity() {
      const quantity = this.selectedQuantity();
      const moon = this.productInfo.querySelector("moonbundle-component");
      const manager = moon?.storeFrontManager;
      const offerId = manager?.selectedOfferId;
      const offerConfig = manager?.config?.offers?.[offerId];
      const offer = moon?.bundle?.offers?.find((item) => item.id === offerId)
        || manager?.instance?.bundle?.offers?.find((item) => item.id === offerId);

      if (!offerConfig || !offer || typeof manager.updateQuantityInForm !== "function") {
        this.updateButtonPrice();
        return false;
      }

      offerConfig.quantityToAdd = quantity;
      manager.updateQuantityInForm(offer);

      /*
       * Moon deliberately hides the theme quantity selector after updating its
       * hidden bundle quantity. Restore our inline control immediately.
       */
      this.disableKaching();
      this.updateButtonPrice();
      return true;
    }

    selectedQuantity() {
      const value = Number(this.quantityInput?.value || 1);
      return Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 1;
    }

    async onSubmit(event) {
      if (this.complementaryPassThrough) {
        this.complementaryPassThrough = false;
        return;
      }

      if (!this.checkbox.checked || this.submitting) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.mode === "exclusive") {
        await this.addExclusiveSubscription();
      } else if (this.mode === "bundle_component") {
        await this.addBundleComponentSubscription(event.submitter);
      } else {
        await this.addComplementarySubscription(event.submitter);
      }
    }

    async addExclusiveSubscription() {
      this.setBusy(true);

      try {
        const response = await this.addSubscriptionLine();
        await this.publishCartChange(response);
      } catch (error) {
        this.publishError(error);
      } finally {
        this.setBusy(false);
      }
    }

    async addComplementarySubscription(submitter) {
      this.setBusy(true);
      let addedLine = null;
      let originalQuantity = 0;

      try {
        const cartBefore = await this.getCart();
        originalQuantity = this.subscriptionQuantity(cartBefore);
        const response = await this.addSubscriptionLine();
        addedLine = response.items?.[0] || response;

        const completion = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            cleanup();
            reject(new Error("L’ajout de l’offre a pris trop de temps. Veuillez réessayer."));
          }, 12000);

          const onSuccess = () => {
            cleanup();
            resolve();
          };

          const onError = (event) => {
            cleanup();
            reject(new Error(event.detail?.error || "Impossible d’ajouter l’offre au panier."));
          };

          const cleanup = () => {
            clearTimeout(timeout);
            this.form.removeEventListener("variant:add", onSuccess);
            this.form.removeEventListener("cart:error", onError);
          };

          this.form.addEventListener("variant:add", onSuccess, { once: true });
          this.form.addEventListener("cart:error", onError, { once: true });
        });

        this.complementaryPassThrough = true;
        /*
         * Keep the buttons disabled while Moon performs its normal submission.
         * Calling requestSubmit without a submitter still triggers Shopify's
         * form handler, whereas a disabled submitter cannot initiate a submit.
         */
        this.form.requestSubmit();
        await completion;
      } catch (error) {
        if (addedLine?.key) {
          await this.restoreSubscriptionQuantity(addedLine.key, originalQuantity).catch(() => {});
        }
        this.publishError(error);
      } finally {
        this.setBusy(false);
      }
    }

    async addBundleComponentSubscription() {
      this.setBusy(true);

      try {
        const cartBefore = await this.getCart();
        const offerId = this.activeMoonOfferId();
        const completion = this.waitForMoonSubmission();

        this.complementaryPassThrough = true;
        this.form.requestSubmit();
        await completion;

        const cartAfterMoon = await this.getCart();
        const cart = await this.convertMoonComponentToSubscription(cartBefore, cartAfterMoon, offerId);
        await this.publishCartSnapshot(cart);
      } catch (error) {
        this.publishError(error);
      } finally {
        this.setBusy(false);
      }
    }

    waitForMoonSubmission() {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error("L’ajout de l’offre a pris trop de temps. Veuillez réessayer."));
        }, 12000);

        const onSuccess = () => {
          cleanup();
          resolve();
        };

        const onError = (event) => {
          cleanup();
          reject(new Error(event.detail?.error || "Impossible d’ajouter l’offre au panier."));
        };

        const cleanup = () => {
          clearTimeout(timeout);
          this.form.removeEventListener("variant:add", onSuccess);
          this.form.removeEventListener("cart:error", onError);
        };

        this.form.addEventListener("variant:add", onSuccess, { once: true });
        this.form.addEventListener("cart:error", onError, { once: true });
      });
    }

    async convertMoonComponentToSubscription(cartBefore, cartAfterMoon, offerId) {
      const targetLine = this.findMoonComponentLine(cartAfterMoon, offerId);
      if (!targetLine) {
        throw new Error("La recharge de l’offre Moon est introuvable dans le panier.");
      }

      const previousQuantity = cartBefore.items
        .filter((item) => this.isSameMoonComponent(item, targetLine, offerId))
        .reduce((total, item) => total + item.quantity, 0);
      const addedQuantity = targetLine.quantity - previousQuantity;

      if (addedQuantity < 1) {
        throw new Error("La recharge ajoutée par Moon n’a pas pu être identifiée.");
      }

      if (previousQuantity === 0) {
        return this.changeCartLine({
          id: targetLine.key,
          quantity: targetLine.quantity,
          selling_plan: Number(this.select.value)
        });
      }

      await this.changeCartLine({
        id: targetLine.key,
        quantity: previousQuantity
      });

      await this.addCartLine({
        id: targetLine.variant_id,
        quantity: addedQuantity,
        selling_plan: Number(this.select.value),
        properties: targetLine.properties || {}
      });

      return this.getCart();
    }

    findMoonComponentLine(cart, offerId) {
      const variantId = Number(this.dataset.variantId);
      const matchingLines = cart.items.filter((item) => (
        item.variant_id === variantId
        && !item.selling_plan_allocation
        && item.properties?.__moonbundle
      ));

      return matchingLines.find((item) => this.moonOfferId(item) === offerId)
        || matchingLines[matchingLines.length - 1];
    }

    isSameMoonComponent(item, targetLine, offerId) {
      return (
        item.variant_id === targetLine.variant_id
        && !item.selling_plan_allocation
        && this.moonOfferId(item) === (offerId || this.moonOfferId(targetLine))
      );
    }

    moonOfferId(item) {
      try {
        return JSON.parse(item.properties?.__moonbundle || "{}").offer_id || "";
      } catch {
        return "";
      }
    }

    activeMoonOfferId() {
      const wrapper = this.productInfo.querySelector(
        "moonbundle-component .moonbundle-option.moonbundle-option-active"
      )?.closest(".moonbundle-option-wrapper");
      return wrapper?.dataset.offerId || "";
    }

    async changeCartLine(change) {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js?moonbundle_ignore=true`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(change)
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || "Impossible de modifier l’abonnement dans le panier.");
      return json;
    }

    async addCartLine(item) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js?moonbundle_ignore=true`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items: [item] })
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || "Impossible d’ajouter l’abonnement au panier.");
      return json;
    }

    async addSubscriptionLine() {
      const option = this.select.selectedOptions[0];
      const sections = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
      );
      const cartDrawerSectionId = this.cartDrawerSectionId();
      if (cartDrawerSectionId && !sections.includes(cartDrawerSectionId)) {
        sections.push(cartDrawerSectionId);
      }

      /*
       * Moon intercepts every cart/add request on products covered by one of
       * its bundles. Its supported ignore flag is required when the selected
       * subscription product is that same product (exclusive mode).
       */
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js?moonbundle_ignore=true`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [{
            id: Number(this.dataset.variantId),
            quantity: this.selectedQuantity(),
            selling_plan: Number(option.value)
          }],
          sections: sections.join(","),
          sections_url: `${window.Shopify.routes.root}products/${location.pathname.split("/").pop()}`
        })
      });

      const json = await response.json();
      if (!response.ok) throw new Error(json.description || "Impossible d’ajouter l’abonnement au panier.");
      return json;
    }

    async publishCartChange(response) {
      const cart = await this.getCart();
      await this.attachCartSections(cart, response.sections || {});
      const usesMoonCartDrawer = this.syncCartDrawers(cart);

      this.form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: response.items || [response],
          cart,
          blockCartDrawerOpening: usesMoonCartDrawer
        }
      }));

      this.dispatchCartChange(cart);
      this.openCartDrawer(usesMoonCartDrawer);
    }

    async publishCartSnapshot(cart) {
      await this.attachCartSections(cart);
      const usesMoonCartDrawer = this.syncCartDrawers(cart);
      this.dispatchCartChange(cart);
      this.openCartDrawer(usesMoonCartDrawer);
    }

    async attachCartSections(cart, initialSections = {}) {
      const cartDrawer = document.querySelector("cart-drawer");
      const cartDrawerSectionId = this.cartDrawerSectionId();
      const sections = { ...initialSections };

      /*
       * Some Moon configurations consume the bundled-sections preparation
       * event before the theme drawer sees it. Fetch the drawer section
       * explicitly when Shopify did not return it with cart/add.
       */
      if (cartDrawerSectionId && !sections[cartDrawerSectionId]) {
        const renderedSections = await fetch(
          `${window.Shopify.routes.root}?sections=${encodeURIComponent(cartDrawerSectionId)}`
        ).then((sectionResponse) => sectionResponse.json());
        Object.assign(sections, renderedSections);
      }

      cart.sections = sections;
      this.hydrateCartDrawer(cartDrawer, sections[cartDrawerSectionId]);
    }

    syncCartDrawers(cart) {
      const moonCartDrawer = document.querySelector("moonbundle-cart-drawer-block");
      const usesMoonCartDrawer = typeof moonCartDrawer?.applyCartToDrawer === "function";

      if (usesMoonCartDrawer) {
        moonCartDrawer.applyCartToDrawer(cart);
      }
      return usesMoonCartDrawer;
    }

    dispatchCartChange(cart) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart
        }
      }));
    }

    openCartDrawer(usesMoonCartDrawer) {
      const moonCartDrawer = document.querySelector("moonbundle-cart-drawer-block");
      if (usesMoonCartDrawer) {
        moonCartDrawer.openDrawer();
      } else if (window.themeVariables?.settings?.cartType === "drawer") {
        document.querySelector("cart-drawer")?.show();
      }
    }

    hydrateCartDrawer(cartDrawer, sectionHtml) {
      if (!cartDrawer || !sectionHtml) return;

      const renderedDrawer = new DOMParser()
        .parseFromString(sectionHtml, "text/html")
        .querySelector("cart-drawer");

      if (renderedDrawer) {
        cartDrawer.replaceChildren(...renderedDrawer.childNodes);
      }
    }

    publishError(error) {
      this.form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { error: error.message || String(error) }
      }));
    }

    async getCart() {
      return (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    }

    cartDrawerSectionId() {
      return document
        .querySelector("cart-drawer")
        ?.closest(".shopify-section")
        ?.id
        ?.replace(/^shopify-section-/, "");
    }

    subscriptionQuantity(cart) {
      const variantId = Number(this.dataset.variantId);
      const planId = Number(this.select.value);
      return cart.items
        .filter((item) => item.variant_id === variantId && item.selling_plan_allocation?.selling_plan?.id === planId)
        .reduce((total, item) => total + item.quantity, 0);
    }

    async restoreSubscriptionQuantity(key, quantity) {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ id: key, quantity })
      });
    }

    setBusy(busy) {
      this.submitting = busy;
      for (const button of this.form.querySelectorAll('[type="submit"]')) {
        button.toggleAttribute("aria-busy", busy);
        button.disabled = busy;
      }
    }

    updateButtonPrice() {
      this.decorateButton();
      if (!this.buttonPrice) return;

      let cents = this.selectedMoonPrice();
      if (this.checkbox.checked) {
        const subscriptionPrice = Number(this.select.selectedOptions[0]?.dataset.price || 0);
        const quantity = this.selectedQuantity();
        if (this.mode === "exclusive") {
          cents = subscriptionPrice * quantity;
        } else if (this.mode === "bundle_component") {
          cents = cents - (this.selectedMoonComponentPrice() * quantity) + (subscriptionPrice * quantity);
        } else {
          cents += subscriptionPrice * quantity;
        }
      }

      if (cents > 0) {
        const formattedPrice = this.formatMoney(cents);
        if (this.buttonPrice.textContent !== formattedPrice) {
          this.buttonPrice.textContent = formattedPrice;
        }
      }
    }

    selectedMoonComponentPrice() {
      const activeWrapper = this.productInfo.querySelector(
        "moonbundle-component .moonbundle-option.moonbundle-option-active"
      )?.closest(".moonbundle-option-wrapper");
      const items = [...(activeWrapper?.querySelectorAll(
        ".moonbundle-multi-product-vertical-layout-item"
      ) || [])];
      if (!items.length) return 0;

      const productTitle = (this.dataset.productTitle || "").trim().toLocaleLowerCase();
      const matchingItem = items.find((item) => (
        item.querySelector(".moonbundle-multi-product-title")
          ?.textContent
          ?.trim()
          ?.toLocaleLowerCase()
          ?.includes(productTitle)
      ));
      const item = matchingItem || items[items.length - 1];
      const currentPrice = item.querySelector(".moonbundle-multi-product-price span");
      return this.parseMoney(currentPrice?.textContent);
    }

    selectedMoonPrice() {
      const active = this.productInfo.querySelector(
        "moonbundle-component .moonbundle-option.moonbundle-option-active .moonbundle-option-single-price"
      );
      if (active) return this.parseMoney(active.textContent) * this.selectedQuantity();

      const formQuantity = Number(this.form.querySelector('[data-moonbundle-quantity="true"]')?.value || 1);
      const currentVariant = this.productInfo.querySelector("moonbundle-component")?.getAttribute("main-product");
      try {
        return JSON.parse(currentVariant).variants[0].price * formQuantity;
      } catch {
        return 0;
      }
    }

    parseMoney(value = "") {
      const normalized = value.replace(/\s/g, "").replace(/[^\d,.-]/g, "");
      if (!normalized) return 0;
      const decimal = normalized.includes(",")
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized;
      return Math.round(Number(decimal) * 100);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || "fr-FR", {
        style: "currency",
        currency: this.dataset.currency || "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    }

    escapeHtml(value) {
      const element = document.createElement("span");
      element.textContent = value || "";
      return element.innerHTML;
    }
  }

  customElements.define("subscription-product-offer", SubscriptionProductOffer);
})();
